import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Stocks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stocksData, setStocksData] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedSession, setSelectedSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStock, setEditStock] = useState({ prizeId: null, prizeLabel: '', maxQuantity: 0, usedQuantity: 0 });

  const [filters, setFilters] = useState({
    city_id: '',
    pdv_id: ''
  });
  const [showGainModeModal, setShowGainModeModal] = useState(false);
  const [selectedSessionForGainMode, setSelectedSessionForGainMode] = useState(null);
  const [newGainMode, setNewGainMode] = useState('');

  const loadStocks = useCallback(async () => {
    try {
      const params = {};
      if (filters.city_id) params.city_id = filters.city_id;
      if (filters.pdv_id) params.pdv_id = filters.pdv_id;

      const response = await api.get('/admin/stocks', { params });
      setStocksData(response.data.sessions);
      setPrizes(response.data.prizes);
    } catch (error) {
      console.error('Error loading stocks:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des stocks' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadPdvs = async () => {
    try {
      const params = {};
      if (filters.city_id) params.city_id = filters.city_id;
      const response = await api.get('/admin/pdv', { params });
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadStocks();
    loadCities();
    loadPdvs();
  }, [user, navigate, loadStocks]);

  // Reload PDVs when city changes
  useEffect(() => {
    loadPdvs();
  }, [filters.city_id]);

  // Inject print-specific CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        /* Force white background everywhere */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
          background: white !important;
          color: black !important;
        }

        /* Hide navbar */
        nav, header, .navbar { display: none !important; }

        /* Hide filters card (first card after header) */
        .card .form-select,
        .card:has(.form-select),
        .filters { display: none !important; }

        /* Hide print button */
        button[onclick*="window.print()"] { display: none !important; }

        /* Hide action column */
        th:last-child, td:last-child { display: none !important; }

        /* Hide back button */
        a[href="/dashboard"] { display: none !important; }

        /* Hide edit modal */
        .modal { display: none !important; }

        /* Hide mode de gain button */
        .btn-outline-light[onclick*="GainMode"] { display: none !important; }

        /* Improve page layout */
        .min-vh-100 {
          min-height: auto !important;
          background: white !important;
        }

        /* Improve cards for printing */
        .card {
          border: 1px solid #000 !important;
          background: white !important;
          page-break-inside: avoid;
          box-shadow: none !important;
        }
        .card-header {
          background: white !important;
          border-bottom: 1px solid #000 !important;
        }

        /* Improve tables for printing */
        .table, .table-dark {
          background-color: #fff !important;
          color: #000 !important;
        }
        .table thead th {
          background-color: #f0f0f0 !important;
          color: #000 !important;
          border: 1px solid #000 !important;
        }
        .table tbody td {
          color: #000 !important;
          border: 1px solid #ccc !important;
          background-color: #fff !important;
        }
        .table tr {
          background-color: #fff !important;
        }
        .table-hover tbody tr:hover {
          background-color: #fff !important;
        }

        /* Ensure all text is black */
        h1, h2, h3, h4, h5, h6, p, span, div, small, td, th {
          color: #000 !important;
        }
        .text-light, .text-white {
          color: #000 !important;
        }

        /* Fix badges */
        .badge {
          border: 1px solid #000 !important;
          color: #000 !important;
          background: #fff !important;
        }

        /* Fix progress bars - convert to simple text */
        .progress {
          display: none !important;
        }

        /* Hide icons */
        .fas, .far, .fab {
          display: none !important;
        }

        /* Hide alert messages */
        .alert { display: none !important; }

        /* Hide spinner */
        .spinner-border { display: none !important; }

        /* Page break */
        .card {
          page-break-inside: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleEditStock = (session, prize) => {
    setSelectedSession(session);
    setEditStock({
      prizeId: prize.prize_type,
      prizeLabel: prize.prize_label,
      maxQuantity: prize.max_quantity,
      usedQuantity: prize.used_quantity
    });
    setShowEditModal(true);
  };

  const handleSaveStock = async () => {
    try {
      // Trouver l'ID du prize_config à partir du prize_type
      const prizeConfigResponse = await api.get('/admin/prizes');
      const prizeConfig = prizeConfigResponse.data.find(p => p.prize_type === editStock.prizeId);

      if (!prizeConfig) {
        setMessage({ type: 'danger', text: 'Configuration de lot non trouvée' });
        return;
      }

      // Utiliser le nouvel endpoint pour modifier le quota journalier
      await api.put(`/admin/daily-quota/${selectedSession.pdv_id}/${prizeConfig.id}`, {
        max_quantity: editStock.maxQuantity,
        used_quantity: editStock.usedQuantity
      });

      setMessage({ type: 'success', text: 'Stock mis à jour avec succès' });
      setShowEditModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const handleChangeGainMode = (session) => {
    setSelectedSessionForGainMode(session);
    setNewGainMode(session.gain_mode || 'PROBABILITY');
    setShowGainModeModal(true);
  };

  const handleSaveGainMode = async () => {
    try {
      await api.put(`/admin/sessions/${selectedSessionForGainMode.session_id}`, {
        gain_mode: newGainMode
      });

      setMessage({ type: 'success', text: 'Mode de gain mis à jour avec succès' });
      setShowGainModeModal(false);
      loadStocks();
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const getStockBadge = (percentage, status) => {
    if (status === 'empty') {
      return <span className="badge bg-danger">Épuisé</span>;
    } else if (status === 'low') {
      return <span className="badge bg-warning text-dark">Critique</span>;
    } else if (status === 'medium') {
      return <span className="badge bg-info">Moyen</span>;
    }
    return <span className="badge bg-success">OK</span>;
  };

  const getStockProgressBar = (percentage, status) => {
    let color = 'bg-success';
    if (status === 'empty') color = 'bg-danger';
    else if (status === 'low') color = 'bg-warning';
    else if (status === 'medium') color = 'bg-info';

    return (
      <div className="progress" style={{ height: '8px', backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <div
          className={`progress-bar ${color}`}
          role="progressbar"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="spinner-border text-warning" role="status"></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: 'rgba(26, 26, 46, 0.95)', padding: '12px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/dashboard" style={{ fontSize: '20px' }}>
            <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
              <i className="fas fa-boxes text-dark" style={{ fontSize: '18px' }}></i>
            </div>
            <span>REAKTOR Stocks</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-white me-3">
              <i className="fas fa-user-shield me-2"></i>{user?.username}
            </span>
            <a href="/dashboard" className="btn btn-outline-light btn-sm">
              <i className="fas fa-arrow-left me-2"></i>Retour
            </a>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="text-white fw-bold mb-1">
              <i className="fas fa-warehouse me-2" style={{ color: '#FFD700' }}></i>
              Gestion des Stocks
            </h2>
            <p className="text-light mb-0">{stocksData?.length || 0} session(s) active(s)</p>
          </div>
          <button
            className="btn btn-warning"
            onClick={() => window.print()}
            style={{ fontWeight: 'bold' }}
          >
            <i className="fas fa-print me-2"></i>Imprimer
          </button>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-light small">Ville</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.city_id}
                  onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label text-light small">Point de Vente</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.pdv_id}
                  onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                  disabled={!filters.city_id}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">{filters.city_id ? 'Sélectionner un PDV' : 'Tous'}</option>
                  {pdvs.map(pdv => (
                    <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Stocks Display */}
        {!stocksData || stocksData.length === 0 ? (
          <div className="card border-0 shadow text-center p-5" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
            <i className="fas fa-box-open fa-4x mb-3" style={{ opacity: 0.3, color: '#FFD700' }}></i>
            <p className="text-light mb-0">Aucune session active trouvée</p>
          </div>
        ) : (
          stocksData.map(session => (
            <div key={session.session_id} className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <div className="card-header p-3" style={{ background: 'rgba(255, 215, 0, 0.1)', borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1 fw-bold" style={{ color: '#FFD700' }}>
                      <i className="fas fa-store me-2"></i>{session.pdv_name}
                    </h5>
                    <small className="text-light">
                      <i className="fas fa-map-marker-alt me-1"></i>{session.city_name}
                      <span className="mx-2">•</span>
                      <i className="fas fa-clock me-1"></i>
                      {session.session_name}
                      <span className="mx-2">•</span>
                      <span className={`badge ${session.gain_mode === 'FORCED' ? 'bg-danger' : session.gain_mode === 'RANDOM' ? 'bg-info' : 'bg-success'}`}>
                        {session.gain_mode === 'FORCED' ? 'Gagnant' : session.gain_mode === 'RANDOM' ? 'Aléatoire' : 'Probabilité'}
                      </span>
                    </small>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={() => handleChangeGainMode(session)}
                    title="Changer le mode de gain"
                  >
                    <i className="fas fa-cog me-1"></i>Mode de gain
                  </button>
                </div>
              </div>
              <div className="card-body p-3">
                <div className="table-responsive">
                  <table className="table table-dark table-hover mb-0">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.3)' }}>
                        <th style={{ color: '#FFD700' }}>Lot</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Stock Initial</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Utilisé</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Restant</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Statut</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Progression</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {session.stocks.map(stock => (
                        <tr key={stock.prize_type}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: stock.color,
                                  borderRadius: '4px',
                                  marginRight: '10px'
                                }}
                              ></div>
                              <div>
                                <div className="fw-semibold">{stock.prize_label}</div>
                                <small className="text-muted">{stock.prize_name}</small>
                              </div>
                            </div>
                          </td>
                          <td className="text-center">{stock.max_quantity}</td>
                          <td className="text-center">{stock.used_quantity}</td>
                          <td className="text-center fw-bold" style={{ color: stock.status === 'empty' ? '#dc3545' : stock.status === 'low' ? '#ffc107' : '#fff' }}>
                            {stock.remaining_quantity}
                          </td>
                          <td className="text-center">{getStockBadge(stock.percentage, stock.status)}</td>
                          <td className="text-center" style={{ minWidth: '150px' }}>
                            {getStockProgressBar(stock.percentage, stock.status)}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleEditStock(session, stock)}
                              title="Modifier le stock"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Stock Modal */}
      {showEditModal && selectedSession && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-edit me-2" style={{ color: '#FFD700' }}></i>
                  Modifier le stock journalier
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Point de Vente</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSession.pdv_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Lot</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editStock.prizeLabel || prizes.find(p => p.type === editStock.prizeId)?.label || editStock.prizeId}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Stock maximum (quota)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editStock.maxQuantity}
                    onChange={(e) => setEditStock({ ...editStock, maxQuantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                  <small className="text-muted">Nombre maximum de lots disponibles pour la journée</small>
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Quantité utilisée</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editStock.usedQuantity}
                    onChange={(e) => setEditStock({ ...editStock, usedQuantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                  <small className="text-muted">Nombre de lots déjà distribués aujourd'hui</small>
                </div>
                <div className="alert alert-info" style={{ background: 'rgba(13, 110, 253, 0.1)', borderColor: 'rgba(13, 110, 253, 0.3)' }}>
                  <small className="text-light">
                    <i className="fas fa-info-circle me-2"></i>
                    Stock restant: <strong>{Math.max(0, editStock.maxQuantity - editStock.usedQuantity)}</strong>
                  </small>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annuler</button>
                <button type="button" className="btn btn-warning" onClick={handleSaveStock}>
                  <i className="fas fa-save me-2"></i>Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gain Mode Modal */}
      {showGainModeModal && selectedSessionForGainMode && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-cog me-2" style={{ color: '#FFD700' }}></i>
                  Changer le mode de gain
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowGainModeModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Point de Vente</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSessionForGainMode.pdv_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Mode de gain actuel</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedSessionForGainMode.gain_mode === 'FORCED' ? 'Gagnant' : selectedSessionForGainMode.gain_mode === 'RANDOM' ? 'Aléatoire' : 'Probabilité'}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light fw-bold">Nouveau mode de gain</label>
                  <select
                    className="form-select"
                    value={newGainMode}
                    onChange={(e) => setNewGainMode(e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  >
                    <option value="PROBABILITY">Probabilité (ratio configuré)</option>
                    <option value="FORCED">Gagnant (100% de gain)</option>
                    <option value="RANDOM">Aléatoire (tirage au sort)</option>
                  </select>
                  <div className="mt-2">
                    {newGainMode === 'PROBABILITY' && (
                      <small className="text-info">
                        <i className="fas fa-info-circle me-1"></i>
                        Chaque lot a sa propre probabilité de gain selon le ratio configuré
                      </small>
                    )}
                    {newGainMode === 'FORCED' && (
                      <small className="text-warning">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        <strong>Tous les joueurs gagnent !</strong> Utilisez ce mode pour les promotions spéciales
                      </small>
                    )}
                    {newGainMode === 'RANDOM' && (
                      <small className="text-muted">
                        <i className="fas fa-random me-1"></i>
                        Tirage aléatoire parmi tous les lots disponibles (probabilité égale)
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowGainModeModal(false)}>Annuler</button>
                <button type="button" className="btn btn-warning" onClick={handleSaveGainMode}>
                  <i className="fas fa-save me-2"></i>Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stocks;
